// Polyfill for toReversed() method (ES2023) for older browsers/Electron
if (!Array.prototype.toReversed) {
  // skipcq: JS-0061
  Array.prototype.toReversed = function () {
    return this.slice().reverse();
  };
}

import { createApp } from 'vue';
import App from './App.vue';
import { isTestEnv } from '@/utils';
import store from '@/store';
import router from './utils/router';
import { createVuetify } from 'vuetify';
// skipcq: JS-C1003
import * as components from 'vuetify/components';
// skipcq: JS-C1003
import * as directives from 'vuetify/directives';
import 'vuetify/styles';
import '@mdi/font/css/materialdesignicons.css';
import './assets/scss/global.scss';
import { AppLogger } from '@/logger';
import snackbarPlugin from '@/utils/plugins/snackbar';
// import * as Sentry from '@sentry/vue';
import { QuillyEditor } from 'vue-quilly';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import interceptorsSetup from '@/utils/helpers/interceptor';

// Types for Window object: Cypress, clarity, basePath
declare global {
  export interface Window {
    basePath: string;
    Cypress: object;
    clarity: (
      methodName: 'identify' | 'consent' | 'event' | 'upgrade' | 'set',
      userId?: string,
      sessionId?: string,
      pageId?: string,
      userName?: string,
    ) => void;
    grecaptcha?: {
      render: (...args: unknown[]) => number;
      ready: (...args: unknown[]) => unknown;
      reset: (...args: unknown[]) => unknown;
      getResponse: (...args: unknown[]) => unknown;
    };
  }
}

// Init App
const app = createApp(App);

app.config.warnHandler = (msg, _instance, trace) => {
  if (msg.includes('invoked outside of the render function')) {
    return;
  }
  console.warn(`[Vue warn]: ${msg}${trace}`);
};

// Router
interceptorsSetup(store, router);
app.use(router);

// Store
app.use(store);

// Vuetify
const vuetify = createVuetify({
  components,
  directives,
  defaults: {
    VTextField: {
      clearIcon: 'mdi-close',
    },
    VSelect: {
      clearIcon: 'mdi-close',
    },
    VAutocomplete: {
      clearIcon: 'mdi-close',
    },
    VCombobox: {
      clearIcon: 'mdi-close',
    },
    VDataTable: {
      itemsPerPage: 15,
    },
    VDataTableServer: {
      itemsPerPage: 15,
    },
  },
  theme: {
    defaultTheme: 'dark',
    themes: {
      light: {
        dark: false,
        colors: {
          background: '#FFFFFF',
          surface: '#F5F5F5',
          primary: '#6200EE',
          secondary: '#03DAC6',
          error: '#B00020',
        },
      },
      dark: {
        dark: true,
        colors: {
          background: '#121212',
          surface: '#1E1E1E',
          secondary: '#03DAC6',
          primary: '#93C856',
          success: '#8DEFC1',
          warning: '#F1CF3B',
          error: '#FB5F62',
          info: '#4870BB',
          disabled: '#DBDBDB',
        },
      },
    },
  },
});
app.provide('vuetify', vuetify);
app.use(vuetify);

// Logger
const logger = new AppLogger('Client root');
const consoleErrorOriginal = console.error;

console.error = (...data: unknown[]): void => {
  consoleErrorOriginal(data);
  if (isTestEnv()) {
    AppLogger.showErrorOnPage(data.toString());
  }
  logger.error('Error from browser console', data, undefined, false);
};

// Snackbar
app.use(snackbarPlugin, { store });

// // Sentry
// if (import.meta.env.VUE_APP_SENTRY_DSN) {
//   Sentry.init({
//     app,
//     dsn: import.meta.env.VUE_APP_SENTRY_DSN,
//     integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
//     tracesSampleRate: 1.0,
//     tracePropagationTargets: ['localhost', 'next.id.cast-soft.com', /^\//],
//     replaysSessionSampleRate: 0.1,
//     replaysOnErrorSampleRate: 1.0,
//   });
// }

// Quill
app.component('QuillyEditor', QuillyEditor);

app.mount('#app');
